/**
 * The words the print panel puts on screen.
 *
 * Extracted from PrintMaps.vue because these are not presentation — they are
 * claims about what the user is about to get, and several of them were wrong
 * before ("one sheet per day" when a fixed scale tiles a long day; "Scale to
 * fit" when the page could work out the actual number). A claim worth making
 * is worth testing, and it cannot be tested inline in a component.
 *
 * Pure functions of explicit inputs: no store, no map, no refs.
 */
import { DEFAULT_ROUTE_STYLE_SETTINGS } from '../routeStyleSettings';
import type { RouteStyleSettings } from '../routeStyleSettings';
import { formatScale } from './printMapGeometry';
import { describeFittedScale, scalesDiffer } from './printScalePlan';
import type { SheetMode } from './printSheetQueue';
import { i18n } from '../../i18n';

const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

/** How many days of a fan-out overflow one sheet, out of how many. */
export interface OverflowDays {
  over: number;
  total: number;
}

/** "1:25k" — the compact form used on the scale buttons. */
export function shortScale(denominator: number): string {
  return denominator >= 1000 ? `1:${Math.round(denominator / 1000)}k` : `1:${denominator}`;
}

/** "Covers 4.5 × 5.9 km of ground", or the fitted-scale stand-in. */
export function coverageHint(coverage: { widthKm: number; heightKm: number } | null): string {
  if (!coverage) return t('utils.print_panel_copy.scale_chosen_to_fit');

  return t('utils.print_panel_copy.covers_km_of_ground', {
    width: coverage.widthKm.toFixed(1),
    height: coverage.heightKm.toFixed(1),
  });
}

export interface ScaleHintInput {
  fitted: boolean;
  mode: SheetMode;
  coverage: string;
  /** Denominators a fitted job will print at — one per day for a fan-out. */
  fittedScales: number[];
  overflow: OverflowDays;
}

/**
 * The line under the scale buttons.
 *
 * Fixed scale: says how much ground a sheet covers, and — the part that used
 * to be a vague "longer days will run to several sheets" — exactly how many
 * days overflow at this scale.
 *
 * Fitted: names the scale, or the range of scales, rather than saying nothing.
 */
export function scaleHint({ fitted, mode, coverage, fittedScales, overflow }: ScaleHintInput): string {
  if (!fitted) {
    if (mode === 'single') return coverage;

    const { over, total } = overflow;
    if (over === 0) return t('utils.print_panel_copy.coverage_each_day_fits', { coverage });
    if (over >= total) return t('utils.print_panel_copy.coverage_every_day_overflows', { coverage });
    return t('utils.print_panel_copy.coverage_some_days_overflow', { coverage, over, total });
  }

  const label = describeFittedScale(fittedScales);

  if (mode === 'per_day') {
    // The honest caveat. Different pages at different scales is the right
    // behaviour — it is what keeps each day on one sheet — but it is a
    // surprise in a printed set unless you are told.
    return scalesDiffer(fittedScales)
      ? t('utils.print_panel_copy.each_day_different_scales', { label })
      : t('utils.print_panel_copy.each_day_same_scale', { label });
  }

  return label
    ? t('utils.print_panel_copy.whole_walk_fitted_at', { label })
    : t('utils.print_panel_copy.whole_walk_fitted');
}

/**
 * The hint on the "Every day" mode option.
 *
 * Only promises one sheet per day when that is actually true — pin a fixed
 * scale and the strip mapper tiles a long day across several.
 */
export function perDayHint({ over, total }: OverflowDays): string {
  if (over === 0) return t('utils.print_panel_copy.one_sheet_per_day');
  if (over >= total) return t('utils.print_panel_copy.framed_every_day_overflows');
  return t('utils.print_panel_copy.framed_some_days_overflow', { over, total });
}

export interface ActionInput {
  mode: SheetMode;
  dayCount: number;
  pileSize: number;
  overflow: OverflowDays;
  /**
   * PDFs the pile comes out as. One unless the user changed page size or
   * orientation partway through collecting, since a single document cannot
   * mix them. Defaults to 1 so callers that never had a pile are unaffected.
   */
  pileJobs?: number;
}

export function primaryActionLabel({ mode, dayCount, pileSize, pileJobs = 1 }: ActionInput): string {
  if (mode === 'per_day') return t('utils.print_panel_copy.print_all_days', { count: dayCount });
  if (mode === 'whole_route') return t('utils.print_panel_copy.print_whole_route');
  // The label sits directly above the hint, so it has to agree with it. Saying
  // "as one PDF" over a hint explaining the pile splits into two is the exact
  // surprise this was meant to remove.
  if (pileSize > 1) {
    return pileJobs > 1
      ? t('utils.print_panel_copy.print_sheets_as_pdfs', { sheets: pileSize, pdfs: pileJobs })
      : t('utils.print_panel_copy.print_sheets_as_one_pdf', { sheets: pileSize });
  }
  if (pileSize === 1) return t('utils.print_panel_copy.print_one_sheet');
  return t('utils.print_panel_copy.print_this_sheet');
}

/** "Printing 4 sheets as one PDF — it will appear in the queue." */
export function combinedSuccessMessage(sheetCount: number, jobCount: number): string {
  return jobCount > 1
    ? t('utils.print_panel_copy.printing_sheets_as_pdfs', { sheets: sheetCount, pdfs: jobCount })
    : t('utils.print_panel_copy.printing_sheets_as_one_pdf', { sheets: sheetCount });
}

/**
 * "3 sheets could not be queued" — counted in sheets, not jobs.
 *
 * `createPrintMaps` reports failures per job index, and a combined pile packs
 * many sheets into one job. Reporting the raw failure count told someone whose
 * twelve-sheet pile failed that "1 sheet(s) could not be queued".
 */
export function failedSheetCount(jobs: Array<{ pages?: unknown }>, failedIndexes: number[]): number {
  return failedIndexes.reduce((total, index) => {
    const pages = jobs[index]?.pages;
    return total + (Array.isArray(pages) ? pages.length : 1);
  }, 0);
}

export function primaryActionHint({ mode, pileSize, overflow, pileJobs = 1 }: ActionInput): string {
  if (mode === 'per_day') {
    // "a page per day" is a promise the fixed-scale path cannot keep.
    return overflow.over === 0
      ? t('utils.print_panel_copy.one_pdf_page_per_day')
      : t('utils.print_panel_copy.one_pdf_at_least_page_per_day');
  }
  if (mode === 'whole_route') return t('utils.print_panel_copy.one_pdf_full_walk');
  if (pileSize) {
    // A pile that straddles page sizes cannot be one document, and finding
    // that out from the queue rather than the button is a nasty surprise.
    return pileJobs > 1
      ? t('utils.print_panel_copy.pile_as_pdfs', { sheets: pileSize, pdfs: pileJobs })
      : t('utils.print_panel_copy.pile_as_one_pdf', { sheets: pileSize });
  }
  return t('utils.print_panel_copy.area_inside_red_box');
}

/** "north 1.4° E of true, sheet rotated to it" — empty when north is up. */
export function gridNorthNote(signedDegrees: number): string {
  if (!signedDegrees) return '';

  const degrees = Math.abs(signedDegrees).toFixed(1);
  return signedDegrees >= 0
    ? t('utils.print_panel_copy.grid_north_east', { degrees })
    : t('utils.print_panel_copy.grid_north_west', { degrees });
}

/** The map readout's headline: the fixed scale, or what fitting will produce. */
export function readoutScale(denominator: number | null, fittedScales: number[]): string {
  if (denominator) return formatScale(denominator);

  return describeFittedScale(fittedScales) || t('utils.print_panel_copy.scale_to_fit');
}

/**
 * The modes available for a given source.
 *
 * A hike with days can fan out; any hike can print whole; anyone can frame a
 * sheet by hand. Below two options there is no question to ask, which is why
 * the panel hides the card entirely.
 */
export interface ModeOption {
  value: SheetMode;
  label: string;
  hint: string;
}

export function availableModes(
  source: { multiDay: boolean; dayCount: number } | null,
  perDayHintText: string,
): ModeOption[] {
  const modes: ModeOption[] = [];

  if (source?.multiDay) {
    modes.push({ value: 'per_day', label: t('utils.print_panel_copy.every_day_count', { count: source.dayCount }), hint: perDayHintText });
  }
  if (source) {
    modes.push({
      value: 'whole_route',
      label: t('utils.print_panel_copy.whole_route'),
      hint: t('utils.print_panel_copy.whole_route_hint'),
    });
  }
  modes.push({
    value: 'single',
    label: t('utils.print_panel_copy.this_map_area'),
    hint: t('utils.print_panel_copy.this_map_area_hint'),
  });

  return modes;
}

export function hasModeChoice(modes: ModeOption[]): boolean {
  return modes.length > 1;
}

/**
 * The summary under "Customise route style", so a changed line is visible
 * with the section folded away.
 *
 * Names the line types that differ from the shipped defaults rather than
 * spelling out colours: hex codes mean nothing on a button, and "customised"
 * alone does not say which of the four it was.
 */
export function routeStyleSummary(settings: RouteStyleSettings): string {
  const changed = ROUTE_STYLE_TAB_LABELS
    .filter(({ key }) => differsFromDefault(settings, key))
    .map(({ label }) => label);

  if (changed.length === 0) return t('utils.print_panel_copy.default_route_style');
  if (changed.length === ROUTE_STYLE_TAB_LABELS.length) return t('utils.print_panel_copy.all_line_types_customised');

  return t('utils.print_panel_copy.line_types_customised', { types: changed.join(', ') });
}

/** The four line types, in the order the editor's tabs show them. */
export const ROUTE_STYLE_TAB_LABELS: Array<{ key: RouteStyleTab; label: string }> = [
  { key: 'main',      get label() { return t('utils.print_panel_copy.line_main'); } },
  { key: 'alt',       get label() { return t('utils.print_panel_copy.line_alt'); } },
  { key: 'spur',      get label() { return t('utils.print_panel_copy.line_spur'); } },
  { key: 'recording', get label() { return t('shared.recording'); } },
];

type RouteStyleTab = keyof Omit<RouteStyleSettings, 'hideMileMarkers'>;

function differsFromDefault(settings: RouteStyleSettings, key: RouteStyleTab): boolean {
  const chosen = settings[key];
  const shipped = DEFAULT_ROUTE_STYLE_SETTINGS[key];

  return chosen.color.toUpperCase() !== shipped.color.toUpperCase()
    || chosen.thickness !== shipped.thickness
    || chosen.opacity !== shipped.opacity;
}
